import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Camera, Upload, Trash2, Star, Loader2, Plus, Image as ImageIcon,
  GripVertical, Save
} from 'lucide-react';
import NamusImage from './NamusImage';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable photo item component
function SortablePhoto({ photo, onSetPrimary, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group rounded-lg overflow-hidden border-2 ${
        photo.is_primary 
          ? 'border-amber-400 ring-2 ring-amber-200' 
          : 'border-slate-200 hover:border-slate-300'
      } ${isDragging ? 'shadow-xl' : ''}`}
    >
      {/* Drag handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        title="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </div>

      <div className="aspect-square">
        <NamusImage
          src={photo.url}
          alt={`Photo ${photo.index + 1}`}
          className="w-full h-full object-cover pointer-events-none"
        />
      </div>
      
      {/* Primary badge */}
      {photo.is_primary && (
        <Badge className="absolute top-2 left-2 bg-amber-500 text-white">
          <Star className="w-3 h-3 mr-1 fill-current" />
          Primary
        </Badge>
      )}

      {/* Actions overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        {!photo.is_primary && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onSetPrimary(photo.index)}
            className="gap-1 h-7 text-xs"
          >
            <Star className="w-3 h-3" />
            Primary
          </Button>
        )}
        <Button
          size="sm"
          variant="destructive"
          onClick={() => onDelete(photo.index)}
          className="h-7"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

export default function PhotoManager({ caseId, isAdmin, onPhotosUpdated }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const fileInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      // Cache-bust the GET so we never read a stale 304 immediately
      // after a write. Some CDNs hold the previous photo response in
      // the browser cache for a few minutes; the timestamp forces a
      // fresh response.
      const response = await fetch(
        `${API_URL}/api/photos/${caseId}/all?_t=${Date.now()}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        // Add unique IDs for drag-drop
        const photosWithIds = (data.photos || []).map((p, i) => ({
          ...p,
          id: `photo-${p.index}-${i}`,
        }));
        setPhotos(photosWithIds);
        setHasOrderChanged(false);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    fetchPhotos();
    setDialogOpen(true);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setPhotos((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        setHasOrderChanged(true);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      // Send the new order to the backend
      const newOrder = photos.map((p, newIdx) => ({
        original_index: p.index,
        new_index: newIdx,
      }));

      const response = await fetch(
        `${API_URL}/api/photos/${caseId}/reorder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          credentials: 'include',
          body: JSON.stringify({ order: newOrder })
        }
      );

      if (response.ok) {
        toast.success('Photo order saved');
        setHasOrderChanged(false);
        await fetchPhotos();
        if (onPhotosUpdated) onPhotosUpdated();
      } else {
        toast.error(await extractError(response, 'Save order failed'));
      }
    } catch (error) {
      console.error('Save order error:', error);
      toast.error(
        error?.message
          ? `Save order failed: ${error.message}`
          : 'Save order failed — check your connection and try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  // Parse a fetch response that may not be JSON (Cloudflare 502/504/413
  // sometimes return HTML). Returns a string for toast display.
  const extractError = async (response, fallback) => {
    const status = response.status;
    try {
      const data = await response.clone().json();
      if (data?.detail) return `${data.detail} (HTTP ${status})`;
      if (data?.message) return `${data.message} (HTTP ${status})`;
    } catch (_) {
      // Body wasn't JSON — Cloudflare interstitial, nginx 413, etc.
    }
    try {
      const text = await response.text();
      const snippet = text.replace(/<[^>]+>/g, '').slice(0, 120).trim();
      if (snippet) return `${fallback} — HTTP ${status}: ${snippet}`;
    } catch (_) {}
    return `${fallback} (HTTP ${status})`;
  };

  const handleUpload = async (event, setAsPrimary = false) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (uploading) return;  // guard against re-entrant clicks

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // 10MB raw cap — the server-side optimizer (Pillow 1600px / Q85 /
    // strip EXIF, see /app/backend/routes/photos.py) will shrink most
    // phone photos by 5–10× before they're stored, so we don't need to
    // be miserly here. The cap exists only to bound worst-case decode
    // memory and bandwidth.
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const url = `${API_URL}/api/photos/${caseId}/upload?set_as_primary=${setAsPrimary}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        toast.success(setAsPrimary ? 'Primary photo updated' : 'Photo added successfully');
        // Refresh the dialog's photo grid AND notify the parent so
        // the case-detail view re-fetches and re-renders the new
        // primary photo. Both reads run AFTER the upload's POST has
        // returned 200, so we never race the write the way the
        // previous window.location.reload() did.
        await fetchPhotos();
        if (onPhotosUpdated) onPhotosUpdated();
      } else {
        toast.error(await extractError(response, 'Upload failed'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(
        error?.message
          ? `Upload failed: ${error.message}`
          : 'Upload failed — check your connection and try again.'
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Reset the 'Replace Primary' input too — without this, picking
      // the same file twice in a row silently no-ops because the
      // change event doesn't fire when the input.value is unchanged.
      const primary = document.getElementById('primary-upload');
      if (primary) primary.value = '';
    }
  };

  const handleDelete = async () => {
    if (photoToDelete === null) return;

    try {
      const response = await fetch(
        `${API_URL}/api/photos/${caseId}/photo/${photoToDelete}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Photo deleted');
        await fetchPhotos();
        if (onPhotosUpdated) onPhotosUpdated();
      } else {
        toast.error(await extractError(response, 'Delete failed'));
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(
        error?.message
          ? `Delete failed: ${error.message}`
          : 'Delete failed — check your connection and try again.'
      );
    } finally {
      setDeleteDialogOpen(false);
      setPhotoToDelete(null);
    }
  };

  const handleSetPrimary = async (photoIndex) => {
    try {
      const response = await fetch(
        `${API_URL}/api/photos/${caseId}/photo/${photoIndex}/primary`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Photo set as primary');
        await fetchPhotos();
        if (onPhotosUpdated) onPhotosUpdated();
      } else {
        toast.error(await extractError(response, 'Set primary failed'));
      }
    } catch (error) {
      console.error('Set primary error:', error);
      toast.error(
        error?.message
          ? `Set primary failed: ${error.message}`
          : 'Set primary failed — check your connection and try again.'
      );
    }
  };

  if (!isAdmin) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpenDialog}
        className="gap-2"
        data-testid="manage-photos-btn"
      >
        <Camera className="w-4 h-4" />
        Manage Photos
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Photo Management
            </DialogTitle>
            <DialogDescription>
              Upload, delete, or drag photos to reorder them
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Upload buttons */}
            <div className="flex flex-wrap gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleUpload(e, false)}
                accept="image/*"
                className="hidden"
                id="photo-upload"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="gap-2"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Add Photo
              </Button>
              <label htmlFor="primary-upload">
                <input
                  type="file"
                  onChange={(e) => handleUpload(e, true)}
                  accept="image/*"
                  className="hidden"
                  id="primary-upload"
                />
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('primary-upload')?.click()}
                  disabled={uploading}
                  className="gap-2"
                  asChild
                >
                  <span>
                    <Upload className="w-4 h-4" />
                    Replace Primary
                  </span>
                </Button>
              </label>
              
              {/* Save order button - only show when order has changed */}
              {hasOrderChanged && (
                <Button
                  onClick={handleSaveOrder}
                  disabled={saving}
                  className="gap-2 bg-green-600 hover:bg-green-700 text-white ml-auto"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Order
                </Button>
              )}
            </div>

            {/* Drag hint */}
            {photos.length > 1 && !hasOrderChanged && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <GripVertical className="w-4 h-4" />
                Drag photos to reorder them
              </p>
            )}

            {/* Photos grid with drag-drop */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : photos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No photos available</p>
                <p className="text-sm">Click "Add Photo" to upload one</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={photos.map(p => p.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid grid-cols-3 gap-3">
                    {photos.map((photo) => (
                      <SortablePhoto
                        key={photo.id}
                        photo={photo}
                        onSetPrimary={handleSetPrimary}
                        onDelete={(idx) => {
                          setPhotoToDelete(idx);
                          setDeleteDialogOpen(true);
                        }}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Photo?</DialogTitle>
            <DialogDescription>
              {photoToDelete === 0 
                ? "This will delete the primary photo. This action cannot be undone."
                : "Are you sure you want to delete this photo? This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
